import React, { useState, useEffect } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useSelector } from "react-redux";
import { selectSettings } from "../store/slices/settingsSlice";
import {
  getPageBackground,
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
} from "../utils/themeUtils";
import { Button, Input } from "../components/ui";
import { getPublicPosts, Post } from "../api/postsAPI";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import SEOHead from "../components/seo/SEOHead";
import BreadcrumbSchema from "../components/seo/BreadcrumbSchema";

const PublicPostsPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const settings: any = useSelector(selectSettings);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const pathParts = location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || null;

  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [categories, setCategories] = useState<string[]>([]);
  // The club has its public blog turned off (settings.blog_enabled): the API
  // answers 404 BLOG_DISABLED and this page sends the visitor home.
  const [blogDisabled, setBlogDisabled] = useState(false);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const currentCategory = searchParams.get("category") || "";
  const currentSearch = searchParams.get("search") || "";
  const [searchInput, setSearchInput] = useState(currentSearch);

  useEffect(() => {
    fetchPosts();
  }, [currentPage, currentCategory, currentSearch]);

  const fetchPosts = async () => {
    setLoading(true);
    const response = await getPublicPosts({
      page: currentPage,
      limit: 9,
      category: currentCategory || undefined,
      search: currentSearch || undefined,
    });
    if (response.code === "BLOG_DISABLED") {
      setBlogDisabled(true);
    } else if (response.success) {
      setPosts(response.data);
      setTotalPages(response.pagination.totalPages);
      // Extract unique categories from posts for filter
      const allCategories = response.data.flatMap((p) => p.categories || []);
      const unique = Array.from(new Set(allCategories));
      if (unique.length > 0 && categories.length === 0) {
        setCategories(unique);
      }
    }
    setLoading(false);
  };

  const updateParams = (updates: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });
    // Reset to page 1 when filters change
    if (!updates.page) params.delete("page");
    setSearchParams(params);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    updateParams({ search: searchInput });
  };

  if (blogDisabled || settings?.blog_enabled === false) {
    return <Navigate to={`/${tenantId}`} replace />;
  }

  return (
    <div className={`flex flex-col min-h-screen ${getPageBackground(isDarkMode)}`}>
      <SEOHead
        title="Blog"
        description={`Read the latest news and updates from ${settings?.business_name || "our club"}.`}
      />
      <BreadcrumbSchema items={[{ name: "Blog", path: "/posts" }]} />
      <Navbar />

      <main className="flex-1">
        {/* Header */}
        <div className="py-12 sm:py-16 bg-gray-900 text-white text-center">
          <div className="container mx-auto px-4">
            <h1 className="text-3xl sm:text-4xl font-bold mb-3">Blog</h1>
            <p className="text-lg opacity-90 max-w-2xl mx-auto">
              News, updates, and insights from our team.
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2 items-start">
              <div className="flex-1">
                <Input
                  aria-label="Search posts"
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search posts..."
                />
              </div>
              <Button type="submit" variant="primary" size="md">
                Search
              </Button>
            </form>

            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => updateParams({ category: "" })}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    !currentCategory
                      ? "bg-[var(--cc-action)] text-[var(--cc-action-text)]"
                      : isDarkMode
                      ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  All
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => updateParams({ category: cat })}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      currentCategory === cat
                        ? "bg-[var(--cc-action)] text-[var(--cc-action-text)]"
                        : isDarkMode
                        ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Posts Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className={`text-lg ${getMutedText(isDarkMode)}`}>Loading posts...</div>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-12">
              <p className={`text-lg ${getMutedText(isDarkMode)}`}>
                {currentSearch || currentCategory
                  ? "No posts match your filters."
                  : "No posts published yet. Check back soon!"}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {posts.map((post) => (
                <Link
                  key={post.id}
                  to={`/${tenantId}/posts/${post.slug}`}
                  className={`${getCardBackground(isDarkMode)} rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow group`}
                >
                  {post.featured_image && (
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-48 object-cover group-hover:opacity-90 transition-opacity"
                    />
                  )}
                  <div className="p-5">
                    {post.categories?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {post.categories.slice(0, 2).map((cat, idx) => (
                          <span
                            key={idx}
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              isDarkMode
                                ? "bg-gray-700 text-gray-300"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                    )}
                    <h2 className={`text-lg font-semibold mb-2 ${getPrimaryText(isDarkMode)} group-hover:underline`}>
                      {post.title}
                    </h2>
                    {post.excerpt && (
                      <p className={`text-sm line-clamp-3 ${getSecondaryText(isDarkMode)}`}>
                        {post.excerpt}
                      </p>
                    )}
                    <div className={`mt-3 text-xs ${getMutedText(isDarkMode)}`}>
                      {post.published_at &&
                        new Date(post.published_at).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-10">
              {currentPage > 1 && (
                <button
                  onClick={() => updateParams({ page: String(currentPage - 1) })}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    isDarkMode
                      ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  Previous
                </button>
              )}
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => updateParams({ page: String(page) })}
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${
                    page === currentPage
                      ? "bg-[var(--cc-action)] text-[var(--cc-action-text)]"
                      : isDarkMode
                      ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {page}
                </button>
              ))}
              {currentPage < totalPages && (
                <button
                  onClick={() => updateParams({ page: String(currentPage + 1) })}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    isDarkMode
                      ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  Next
                </button>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PublicPostsPage;
